'use client';

import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
} from 'react';
// import { BloodVisionPlan } from './buy/types/bloodVision';
import {
  IndiaPlan,
  IndiaCategory,
  IndiaPincodeInfo,
  IndiaPackageListResponse,
} from './buy/india/types';
import {
  DateSlot as SlotData,
  fetchSlotsAvailability,
  transformApiSlotsToDateSlots,
} from './buy/india/slotBookingTypes';
import { API_BASE_URL } from 'lib/config/apiEndpoints';
import { usePathname, useSearchParams } from 'next/navigation';

// India-specific checkout form interface
export interface IndiaCheckoutForm {
  firstName: string;
  lastName: string;
  gender: 'male' | 'female' | 'other';
  dateOfBirth: string;
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  landmark: string;
  city: string;
  state: string;
  pincode: string;
}

// Cart item with quantity support (using IndiaPlan now)
export interface CartItem {
  plan: IndiaPlan;
  quantity: number;
}

// Define the India context state type
interface BloodVisionIndiaContextState {
  // Plans data
  categories: IndiaCategory[];
  availablePlans: IndiaPlan[];
  pincodeInfo: IndiaPincodeInfo | null;
  isLoading: boolean;
  error: string | null;

  // Pincode state
  hasUserProvidedPincode: boolean; // Track if user has entered a pincode

  // Cart management
  cartItems: Map<number, CartItem>;
  addPlanToCart: (plan: IndiaPlan) => void;
  removePlanFromCart: (planId: number) => void;
  updatePlanQuantity: (planId: number, quantity: number) => void;
  clearCart: () => void;
  isPlanInCart: (planId: number) => boolean;

  // Pricing
  subtotal: number;
  discount: number;
  total: number;
  currencySymbol: string;

  // UI State Management
  showBottomBar: boolean;
  showUpsellModal: boolean;
  setShowUpsellModal: (show: boolean) => void;
  showCheckoutForm: boolean;
  setShowCheckoutForm: (show: boolean) => void;
  showNotServiceableModal: boolean;
  setShowNotServiceableModal: (show: boolean) => void;

  // Checkout form data
  checkoutData: IndiaCheckoutForm;
  updateCheckoutData: (data: Partial<IndiaCheckoutForm>) => void;
  resetCheckoutData: () => void;

  // Slot booking data (memoized by pincode)
  slotData: SlotData[];
  isLoadingSlots: boolean;
  slotsError: string | null;
  fetchSlots: () => Promise<void>;

  // API calls
  fetchPlansIndia: (
    pincode?: string,
    isUserProvided?: boolean,
  ) => Promise<void>;
  submitOrder: () => Promise<void>;
  initiatePurchase: (
    selectedDate: string,
    selectedTimeDisplay: string,
  ) => Promise<void>;

  // Payment state
  isProcessingPayment: boolean;
  paymentError: string | null;
}

// Create the context
const BloodVisionIndiaContext = createContext<
  BloodVisionIndiaContextState | undefined
>(undefined);

// Provider props type
interface BloodVisionIndiaProviderProps {
  children: ReactNode;
}

// 'storage' only fires cross-tab; dispatch this so the same-tab header count updates.
const dispatchBloodVisionCartUpdated = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('bloodVisionCartUpdated'));
  }
};

// Initial checkout form data
const initialCheckoutData: IndiaCheckoutForm = {
  firstName: '',
  lastName: '',
  gender: 'male',
  dateOfBirth: '',
  email: '',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  landmark: '',
  city: '',
  state: '',
  pincode: '',
};

// Provider component
export const BloodVisionIndiaProvider: React.FC<
  BloodVisionIndiaProviderProps
> = ({ children }) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // State management - initialize pincode from localStorage if available
  const [categories, setCategories] = useState<IndiaCategory[]>([]);
  const [availablePlans, setAvailablePlans] = useState<IndiaPlan[]>([]);
  const [pincodeInfo, setPincodeInfo] = useState<IndiaPincodeInfo | null>(
    () => {
      if (typeof window === 'undefined') return null;

      try {
        const savedPincode = localStorage.getItem('bloodVisionIndiaPincode');
        if (savedPincode) {
          return JSON.parse(savedPincode);
        }
      } catch (err) {
        console.error('Error loading pincode from localStorage:', err);
      }
      return null;
    },
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // Initialize hasUserProvidedPincode based on localStorage
  // Only set to true if user had provided a NON-DEFAULT pincode in a previous session
  const [hasUserProvidedPincode, setHasUserProvidedPincode] = useState<boolean>(
    () => {
      if (typeof window === 'undefined') return false;

      try {
        const savedPincode = localStorage.getItem('bloodVisionIndiaPincode');
        if (savedPincode) {
          const parsed = JSON.parse(savedPincode);
          // IMPORTANT: Only enable CTAs if:
          // 1. Pincode exists and is not the default pincode (560001)
          // 2. AND is_default_pincode flag is false
          const isNonDefaultPincode =
            parsed.pincode && parsed.pincode !== '560001';
          return isNonDefaultPincode && parsed.is_default_pincode === false;
        }
      } catch (err) {
        console.error('Error checking pincode from localStorage:', err);
      }
      return false;
    },
  );

  // Cart state - using Map for O(1) lookup and updates
  // Initialize from localStorage if available
  const [cartItems, setCartItems] = useState<Map<number, CartItem>>(() => {
    if (typeof window === 'undefined') return new Map();

    try {
      const savedCart = localStorage.getItem('bloodVisionIndiaCart');
      if (savedCart) {
        const parsed = JSON.parse(savedCart);
        // Filter out invalid items with proper validation
        const validEntries = Object.entries(parsed).filter(
          ([, value]: [string, any]) => {
            return value?.plan?.price_details?.price !== undefined;
          },
        );
        return new Map(
          validEntries.map(([key, value]) => [Number(key), value as CartItem]),
        );
      }
    } catch (err) {
      console.error('Error loading cart from localStorage:', err);
      // Clear corrupted localStorage data
      localStorage.removeItem('bloodVisionIndiaCart');
    }
    return new Map();
  });

  // UI state
  const [showUpsellModal, setShowUpsellModal] = useState<boolean>(false);
  const [showCheckoutForm, setShowCheckoutForm] = useState<boolean>(false);
  const [showNotServiceableModal, setShowNotServiceableModal] =
    useState<boolean>(false);

  // Checkout form state - initialize from localStorage if available
  const [checkoutData, setCheckoutData] = useState<IndiaCheckoutForm>(() => {
    if (typeof window === 'undefined') return initialCheckoutData;

    try {
      const savedData = localStorage.getItem('bloodVisionIndiaCheckoutData');
      if (savedData) {
        return JSON.parse(savedData);
      }
    } catch (err) {
      console.error('Error loading checkout data from localStorage:', err);
    }
    return initialCheckoutData;
  });

  // Payment state
  const [isProcessingPayment, setIsProcessingPayment] =
    useState<boolean>(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  // Ref for debouncing localStorage writes
  const saveCartTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Slot booking state
  const [slotData, setSlotData] = useState<SlotData[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState<boolean>(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [slotsCacheKey, setSlotsCacheKey] = useState<string>('');

  // Calculate if bottom bar should show (when cart has items)
  const showBottomBar = cartItems.size > 0;

  // Currency symbol for India
  const currencySymbol = '₹';

  // Calculate pricing
  const subtotal = React.useMemo(() => {
    let total = 0;
    cartItems.forEach((item) => {
      if (item?.plan?.price_details?.price) {
        total += item.plan.price_details.price * item.quantity;
      }
    });
    return total;
  }, [cartItems]);

  // Discount calculation (can be enhanced with promo codes later)
  const discount = React.useMemo(() => {
    // TODO: Implement discount logic based on promo codes or campaigns
    return 0;
  }, [subtotal]);

  // Final total
  const total = subtotal - discount;

  // Fetch available plans for India
  const fetchPlansIndia = useCallback(
    async (pincode?: string, isUserProvided = false) => {
      setIsLoading(true);
      setError(null);

      try {
        // Build API URL - use default pincode 560001 if none provided
        const pincodeToUse = pincode || '560001';

        // Check if pincode has changed - if yes, clear the cart
        if (isUserProvided && pincode) {
          try {
            const existingPincodeData = localStorage.getItem(
              'bloodVisionIndiaPincode',
            );
            if (existingPincodeData) {
              const parsed = JSON.parse(existingPincodeData);
              // If pincode has changed, clear the cart
              if (parsed.pincode && parsed.pincode !== pincode) {
                console.log('[Cart] Pincode changed - clearing cart');
                setCartItems(new Map()); // Clear cart in state
                localStorage.removeItem('bloodVisionIndiaCart'); // Clear cart in localStorage
                dispatchBloodVisionCartUpdated();
              }
            }
          } catch (err) {
            console.error('Error checking existing pincode:', err);
          }
        }

        const apiUrl = `/api/blood-vision/india/package_list/?pincode=${pincodeToUse}`;

        if (process.env.NODE_ENV === 'development') {
          console.log(
            'Fetching India plans from:',
            apiUrl,
            'isUserProvided:',
            isUserProvided,
          );
        }

        // Fetch data from our API route
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: IndiaPackageListResponse = await response.json();

        if (process.env.NODE_ENV === 'development') {
          console.log('India package list response:', data);
        }

        if (data.status === 'ok' && data.data) {
          if (process.env.NODE_ENV === 'development') {
            console.log('India package list loaded:', data.data);
          }

          // Set categories
          setCategories(data.data.categories);

          // Flatten all plans from all categories
          const allPlans = data.data.categories.flatMap(
            (category) => category.plans,
          );
          setAvailablePlans(allPlans);

          // Set pincode info
          const pincodeData = {
            pincode: data.data.pincode,
            is_default_pincode: data.data.is_default_pincode,
            pincode_serviceable: data.data.pincode_serviceable,
            pincode_info: data.data.pincode_info,
          };
          setPincodeInfo(pincodeData);

          // Check if pincode is not serviceable and show modal
          if (isUserProvided && !data.data.pincode_serviceable) {
            console.log('Pincode not serviceable - showing modal');
            setShowNotServiceableModal(true);
          }

          // Persist pincode info to localStorage
          if (typeof window !== 'undefined') {
            try {
              localStorage.setItem(
                'bloodVisionIndiaPincode',
                JSON.stringify(pincodeData),
              );
            } catch (err) {
              console.error('Error saving pincode to localStorage:', err);
            }
          }

          // Update hasUserProvidedPincode flag based on whether user explicitly provided it
          setHasUserProvidedPincode(isUserProvided);

          if (process.env.NODE_ENV === 'development') {
            console.log(
              `Loaded ${allPlans.length} plans from ${data.data.categories.length} categories`,
            );
          }
        } else {
          throw new Error(data.error || 'Invalid response format');
        }
      } catch (err) {
        console.error('Error loading India plans:', err);
        setError('Failed to load plans. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  // Cart management functions
  const addPlanToCart = useCallback((plan: IndiaPlan) => {
    setCartItems((prev) => {
      const newCart = new Map(prev);
      const existing = newCart.get(plan.id);

      if (existing) {
        // Increment quantity
        newCart.set(plan.id, {
          plan,
          quantity: existing.quantity + 1,
        });
      } else {
        // Add new item
        newCart.set(plan.id, {
          plan,
          quantity: 1,
        });
      }

      return newCart;
    });
  }, []);

  const removePlanFromCart = useCallback((planId: number) => {
    setCartItems((prev) => {
      const newCart = new Map(prev);
      newCart.delete(planId);
      return newCart;
    });
  }, []);

  const updatePlanQuantity = useCallback(
    (planId: number, quantity: number) => {
      if (quantity <= 0) {
        removePlanFromCart(planId);
        return;
      }

      setCartItems((prev) => {
        const newCart = new Map(prev);
        const existing = newCart.get(planId);

        if (existing) {
          newCart.set(planId, {
            ...existing,
            quantity,
          });
        }

        return newCart;
      });
    },
    [removePlanFromCart],
  );

  const clearCart = useCallback(() => {
    setCartItems(new Map());
    if (typeof window !== 'undefined') {
      localStorage.removeItem('bloodVisionIndiaCart');
      dispatchBloodVisionCartUpdated();
    }
  }, []);

  const isPlanInCart = useCallback(
    (planId: number) => {
      return cartItems.has(planId);
    },
    [cartItems],
  );

  // Checkout form management
  const updateCheckoutData = useCallback((data: Partial<IndiaCheckoutForm>) => {
    setCheckoutData((prev) => {
      const updated = {
        ...prev,
        ...data,
      };

      // Persist to localStorage
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(
            'bloodVisionIndiaCheckoutData',
            JSON.stringify(updated),
          );
        } catch (err) {
          console.error('Error saving checkout data to localStorage:', err);
        }
      }

      return updated;
    });
  }, []);

  const resetCheckoutData = useCallback(() => {
    setCheckoutData(initialCheckoutData);
    if (typeof window !== 'undefined') {
      localStorage.removeItem('bloodVisionIndiaCheckoutData');
    }
  }, []);

  // Fetch slots availability with memoization based on pincode and cart
  const fetchSlots = useCallback(async () => {
    // Create cache key based on pincode and cart items
    const email = checkoutData.email || 'ayushb147@gmail.com';
    const pincode = checkoutData.pincode;
    const packageIds = Array.from(cartItems.values()).flatMap((item) =>
      Array(item.quantity).fill(item.plan.id),
    );
    const cacheKey = `${pincode}_${packageIds.join('_')}`;

    // If cache key matches and we already have data, skip fetch
    if (slotsCacheKey === cacheKey && slotData.length > 0) {
      console.log('Using cached slot data');
      return;
    }

    if (cartItems.size === 0) {
      setSlotsError('No items in cart');
      setIsLoadingSlots(false);
      return;
    }

    if (!pincode) {
      setSlotsError('Pincode is required');
      setIsLoadingSlots(false);
      return;
    }

    try {
      setIsLoadingSlots(true);
      setSlotsError(null);

      // Use current timestamp
      const currentTimestamp = Math.floor(Date.now() / 1000);

      // Fetch slots from API
      const response = await fetchSlotsAvailability({
        email: email,
        datetime: currentTimestamp,
        packageIds: packageIds,
        pincode: pincode,
      });

      // Transform API response to our format
      const transformedSlots = transformApiSlotsToDateSlots(response);
      setSlotData(transformedSlots);
      setSlotsCacheKey(cacheKey);

      // If no slots available
      if (transformedSlots.length === 0) {
        setSlotsError(
          'No slots available at this time. Please try again later.',
        );
      }
    } catch (error) {
      console.error('Error loading slots:', error);
      setSlotsError(
        error instanceof Error
          ? error.message
          : 'Failed to load available slots. Please try again.',
      );
    } finally {
      setIsLoadingSlots(false);
    }
  }, [
    checkoutData.email,
    checkoutData.pincode,
    cartItems,
    slotsCacheKey,
    slotData.length,
  ]);

  // Submit order and redirect to payment gateway
  const submitOrder = useCallback(async () => {
    if (cartItems.size === 0) {
      setPaymentError('Cart is empty');
      return;
    }

    setIsProcessingPayment(true);
    setPaymentError(null);

    try {
      // Prepare order data
      const planIds = Array.from(cartItems.values()).flatMap((item) =>
        Array(item.quantity).fill(item.plan.id),
      );

      const orderPayload = {
        product_type: 'blood_vision',
        plan_ids: planIds,
        country: 'in',
        user_details: checkoutData,
        success_url:
          window.location.origin +
          `/blood-vision/order/?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: window.location.href,
        metadata: {
          ...Object.fromEntries(searchParams?.entries() ?? []),
        },
      };

      const response = await fetch(
        `${API_BASE_URL}/api/v1/india/subscriptions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Platform: 'web',
          },
          body: JSON.stringify(orderPayload),
        },
      );

      const data = await response.json();

      if (!response.ok || data.status === 'error') {
        const errorMsg =
          typeof data.error === 'object'
            ? data.error?.message
            : data.error;
        throw new Error(
          errorMsg || 'Failed to process order. Please try again.',
        );
      }

      if (data.data?.checkout_url) {
        // Redirect to payment gateway
        window.location.href = data.data.checkout_url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Error submitting order:', err);
      setPaymentError(
        err instanceof Error
          ? err.message
          : 'Failed to process order. Please try again.',
      );
    } finally {
      setIsProcessingPayment(false);
    }
  }, [cartItems, checkoutData, searchParams]);

  // Initiate purchase with selected slot and redirect to payment gateway
  const initiatePurchase = useCallback(
    async (selectedDate: string, selectedTimeDisplay: string) => {
      if (cartItems.size === 0) {
        setPaymentError('Cart is empty');
        return;
      }

      setIsProcessingPayment(true);
      setPaymentError(null);

      try {
        // Find the selected slot object to extract id, epoch_slot, and lab_id
        const dateSlot = slotData.find((slot) => slot.date === selectedDate);
        if (!dateSlot) {
          throw new Error('Selected date not found');
        }

        const selectedSlot = dateSlot.slots.find(
          (slot) => slot.displayTime === selectedTimeDisplay,
        );

        if (!selectedSlot) {
          throw new Error('Selected time slot not found');
        }

        if (!selectedSlot.epochSlot) {
          throw new Error('Invalid slot data: missing epoch timestamp');
        }

        if (!selectedSlot.labId) {
          throw new Error('Invalid slot data: missing lab ID');
        }

        // Expand package IDs based on quantity
        const packageIds = Array.from(cartItems.values()).flatMap((item) =>
          Array(item.quantity).fill(item.plan.id),
        );

        // Remove country code from phone number if present
        // Indian numbers should be 10 digits without +91
        let phoneNumber = checkoutData.phone.trim();
        phoneNumber = phoneNumber.replace(/^\+91\s*/, ''); // Remove +91 prefix
        phoneNumber = phoneNumber.replace(/^91\s*/, ''); // Remove 91 prefix
        phoneNumber = phoneNumber.replace(/\D/g, ''); // Remove non-digit characters

        // Construct the purchase payload
        const purchasePayload = {
          email: checkoutData.email,
          phone: phoneNumber,
          first_name: checkoutData.firstName,
          last_name: checkoutData.lastName,
          gender: checkoutData.gender,
          date_of_birth: checkoutData.dateOfBirth,
          address_line1: checkoutData.addressLine1,
          address_line2: checkoutData.addressLine2,
          landmark: checkoutData.landmark,
          city: checkoutData.city,
          state: checkoutData.state,
          pincode: checkoutData.pincode,
          package_ids: packageIds.join(','), // API expects comma-separated string
          slot_id: selectedSlot.id,
          slot_timestamp: selectedSlot.epochSlot.toString(),
          lab_id: selectedSlot.labId,
        };

        console.log('Initiating purchase with slot:', {
          date: selectedDate,
          time: selectedTimeDisplay,
          slotId: selectedSlot.id,
          labId: selectedSlot.labId,
          packageCount: packageIds.length,
        });

        // Call initiate purchase API
        const response = await fetch(
          '/api/blood-vision/india/initiate_purchase',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(purchasePayload),
          },
        );

        const data = await response.json();

        if (!response.ok || data.status === 'error') {
          const errorMsg =
            typeof data.error === 'object'
              ? data.error?.message
              : data.error;
          throw new Error(
            errorMsg || 'Failed to initiate purchase. Please try again.',
          );
        }

        if (data.data?.payment_link_url) {
          // Redirect to payment gateway
          console.log('Redirecting to payment link URL');
          window.location.href = data.data.payment_link_url;
        } else {
          throw new Error('No payment link URL returned');
        }
      } catch (err) {
        console.error('Error initiating purchase:', err);
        setPaymentError(
          err instanceof Error
            ? err.message
            : 'Failed to initiate purchase. Please try again.',
        );
        setIsProcessingPayment(false);
      }
    },
    [cartItems, checkoutData, slotData],
  );

  // Reset state if localStorage is empty (coming back from other pages)
  useEffect(() => {
    if (searchParams) {
      const hasLocalStorageData = localStorage.getItem(
        'bloodVisionIndiaPincode',
      );

      // If localStorage is empty but state has data, reset everything
      if (
        !hasLocalStorageData &&
        (pincodeInfo || cartItems.size > 0 || hasUserProvidedPincode)
      ) {
        console.log('[BloodVision] localStorage empty - resetting state');
        setPincodeInfo(null);
        setCartItems(new Map());
        setHasUserProvidedPincode(false);
        setAvailablePlans([]);
        setCategories([]);
      }
    }
  }, [searchParams, pathname]); // Check on route changes

  // Fetch plans on mount - only if not already loaded
  useEffect(() => {
    if (searchParams && availablePlans.length === 0 && !isLoading) {
      // Use stored pincode if available, otherwise use default
      const storedPincode = pincodeInfo?.pincode;
      // Check if stored pincode is user-provided (not default)
      // Only set isUserProvided to true if it's a non-default pincode
      const isNonDefaultPincode = !!storedPincode && storedPincode !== '560001';
      const isUserProvided =
        isNonDefaultPincode && pincodeInfo?.is_default_pincode === false;
      fetchPlansIndia(storedPincode, isUserProvided);
    }
  }, [searchParams, pincodeInfo]); // Depend on pincodeInfo to refetch if cleared

  // Close modals when cart is empty
  useEffect(() => {
    if (cartItems.size === 0) {
      setShowUpsellModal(false);
      setShowCheckoutForm(false);
    }
  }, [cartItems.size]);

  // Persist cart to localStorage with debouncing (300ms delay)
  // This reduces I/O operations and improves performance during rapid cart updates
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Clear existing timeout
    if (saveCartTimeoutRef.current) {
      clearTimeout(saveCartTimeoutRef.current);
    }

    // Set new timeout to save after 300ms of inactivity
    saveCartTimeoutRef.current = setTimeout(() => {
      try {
        // Convert Map to object for JSON serialization
        const cartObj = Object.fromEntries(cartItems);
        localStorage.setItem('bloodVisionIndiaCart', JSON.stringify(cartObj));
        dispatchBloodVisionCartUpdated();

        // Only log in development
        if (process.env.NODE_ENV === 'development') {
          console.log('Cart saved to localStorage:', {
            itemCount: cartItems.size,
            subtotal,
            total,
          });
        }
      } catch (err) {
        console.error('Error saving cart to localStorage:', err);
      }
    }, 300);

    // Cleanup timeout on unmount
    return () => {
      if (saveCartTimeoutRef.current) {
        clearTimeout(saveCartTimeoutRef.current);
      }
    };
  }, [cartItems, subtotal, total]);

  // Context value - memoized to prevent unnecessary re-renders
  const contextValue: BloodVisionIndiaContextState = useMemo(
    () => ({
      categories,
      availablePlans,
      pincodeInfo,
      isLoading,
      error,
      hasUserProvidedPincode,
      cartItems,
      addPlanToCart,
      removePlanFromCart,
      updatePlanQuantity,
      clearCart,
      isPlanInCart,
      subtotal,
      discount,
      total,
      currencySymbol,
      showBottomBar,
      showUpsellModal,
      setShowUpsellModal,
      showCheckoutForm,
      setShowCheckoutForm,
      showNotServiceableModal,
      setShowNotServiceableModal,
      checkoutData,
      updateCheckoutData,
      resetCheckoutData,
      slotData,
      isLoadingSlots,
      slotsError,
      fetchSlots,
      fetchPlansIndia,
      submitOrder,
      initiatePurchase,
      isProcessingPayment,
      paymentError,
    }),
    [
      categories,
      availablePlans,
      pincodeInfo,
      isLoading,
      error,
      hasUserProvidedPincode,
      cartItems,
      addPlanToCart,
      removePlanFromCart,
      updatePlanQuantity,
      clearCart,
      isPlanInCart,
      subtotal,
      discount,
      total,
      showBottomBar,
      showUpsellModal,
      setShowUpsellModal,
      showCheckoutForm,
      setShowCheckoutForm,
      showNotServiceableModal,
      setShowNotServiceableModal,
      checkoutData,
      updateCheckoutData,
      resetCheckoutData,
      slotData,
      isLoadingSlots,
      slotsError,
      fetchSlots,
      fetchPlansIndia,
      submitOrder,
      initiatePurchase,
      isProcessingPayment,
      paymentError,
    ],
  );

  return (
    <BloodVisionIndiaContext.Provider value={contextValue}>
      {children}
    </BloodVisionIndiaContext.Provider>
  );
};

// Custom hook to use the India context
export const useBloodVisionIndia = (): BloodVisionIndiaContextState => {
  const context = useContext(BloodVisionIndiaContext);
  if (context === undefined) {
    throw new Error(
      'useBloodVisionIndia must be used within a BloodVisionIndiaProvider',
    );
  }
  return context;
};
